import { OPERACION } from "@/domain/comprobantes/config";
import store from '@/store/index'

export default class DetalleComprobanteBase {
    constructor(data = {}, config = {}) {
        // Mantenemos la referencia al objeto config del padre/store
        this.config = config;

        this.id = data.id || null;
        this.producto = data.producto || null;
        this.promocion = data.promocion || null;
        this.isProducto = data.isProducto ?? !!this.producto;

        this.descripcion_concepto = data.descripcion_concepto || "";
        this.cantidad = Number(data.cantidad ?? 1);

        // PRECIO UNITARIO: Neto (sin IVA)
        this.precioUnitario = Number(data.precioUnitario ?? 0);

        // PRECIO VENTA: Final (con IVA)
        this.precioVenta = Number(data.precioVenta ?? this.precioUnitario);

        this.vat_type = data.vat_type || null;
        // PROPIEDAD CLAVE: Guardamos el subtotal que viene del servidor
        // Si viene de 'fromApi', tendrá valor. Si viene de 'desdeProducto', será null.
        this._subtotalFijo = data.subtotalConIva || null;

        this.normalizar();
    }

    /* =========================
     * CÁLCULOS REACTIVOS (Mirando this.config)
     * ========================= */

    get precioBaseCalculo() {
        const valor = this.config.precioVentaManual
            ? this.precioVenta
            : this.precioUnitario;

        if (isNaN(valor)) console.error(`❌ precioBaseCalculo es NaN para: ${this.descripcion_concepto}`);
        return valor;
    }

    get subtotalNeto() {
        const resultado = this.redondear(this.precioBaseCalculo * this.cantidad);
        console.log(`%c 📦 Neto: ${this.descripcion_concepto}`, 'color: #2196f3', {
            precio: this.precioBaseCalculo,
            cantidad: this.cantidad,
            total: resultado
        });
        return resultado;
    }

    get montoIva() {
        if (!this.config.withIva || !this.vat_type || this.config.precioVentaManual) {
            return 0;
        }

        const ivaPorcentaje = this.getIvaValue();
        const resultado = this.redondear(this.subtotalNeto * (ivaPorcentaje / 100));

        console.log(`%c 💰 IVA: ${ivaPorcentaje}%`, 'color: #ff9800', {
            montoIva: resultado
        });
        return resultado;
    }

    get subtotalConIva() {
        // Si el objeto fue creado desde la API, devolvemos el valor guardado
        if (this._subtotalFijo !== null) {
            return this.redondear(Number(this._subtotalFijo));
        }

        // Si es un item nuevo, ejecutamos la lógica de cálculo
        const neto = this.subtotalNeto;
        const iva = this.montoIva;
        return this.redondear(neto + iva);
    }

    get subtotalSinIva() {
        return this.subtotalNeto;
    }

    /* =========================
     * FACTORIES (Lógica de creación)
     * ========================= */

    static resolvePrecio(producto, config) {
        if (config.operacion === OPERACION.COMPRA) return producto.precioCosto;
        if (config.operacion === OPERACION.VENTA) return producto.precioBase ?? producto.precio_base;
        return 0;
    }

    static resolveIva(producto, config) {
        if (!config.withIva) return null;
        return config.operacion === OPERACION.COMPRA ? producto.iva_compra : producto.iva;
    }

    static _esPizza(producto) {
        const nombre = producto?.categoriaArbol?.name ?? producto?.categoria?.name;
        return nombre?.includes("PIZZA") ?? false;
    }

    static desdeProducto(producto, config = {}, cantidad = null) {
        const esPizza = this._esPizza(producto);
        const cantidadInicial = esPizza ? 0.5 : (cantidad ?? 1);
        const vat = this.resolveIva(producto, config);
        const precioUnitario = this.resolvePrecio(producto, config);
        const detalle = new DetalleComprobanteBase({
            producto,
            isProducto: true,
            descripcion_concepto: producto.nombre || producto.descripcion,
            cantidad: cantidadInicial,
            precioUnitario: precioUnitario,
            precioVenta: producto.precio_venta,
            vat_type: vat
        }, config);
        console.log("Detalle creado desde producto:", detalle);
        return detalle
    }

    static desdePromocion(promocion, config = {}) {
        return new DetalleComprobanteBase({
            promocion,
            isProducto: false,
            descripcion_concepto: promocion.descripcion,
            cantidad: 1,
            precioUnitario: promocion.precioVenta,
            precioVenta: promocion.precioVenta,
            vat_type: { description: "0" }
        }, config);
    }

    static fromApi(data, config = {}) {
        console.log("Desde fromApi es data: ", data);
        
        return new DetalleComprobanteBase({
            id: data.id,
            producto: data.producto,
            isProducto: !!data.producto,
            descripcion_concepto: data.descripcion_concepto,
            cantidad: Number(data.cantidad),
            precioUnitario: Number(data.precioUnitarioSinIva ?? data.precioUnitario),
            precioVenta: data.producto?.precio_venta ?? data.precioVenta,
            vat_type: data.vat_type ? { description: String(data.vat_type.description) } : null,
            promocion: data.promocion ?? null,
            subtotalConIva: data.subtotal, 

        }, config);
    }

    /* =========================
     * LÓGICA DE NEGOCIO
     * ========================= */

    normalizar() {
        this.normalizarCantidad();
        this.normalizarPrecios();
    }

    normalizarCantidad() {
        if (DetalleComprobanteBase._esPizza(this.producto)) {
            this.cantidad = this.cantidad < 0.5 ? 0.5 : Math.round(this.cantidad * 2) / 2;
        } else {
            if (!this.cantidad || this.cantidad <= 0) this.cantidad = 1;
        }
    }

    normalizarPrecios() {
        this.precioUnitario = Number(this.precioUnitario) || 0;
        this.precioVenta = Number(this.precioVenta) || this.precioUnitario;
    }

    getPasoCantidad() {
        return DetalleComprobanteBase._esPizza(this.producto) ? 0.5 : 1;
    }

   incrementar(delta = null) {
    const paso = this.getPasoCantidad();
    const incremento = delta == null ? paso : delta * paso;
    const nuevaCantidad = this.cantidad + incremento;

    // 1. Extraemos las banderas de control
    const moduloInventarioActivo = store.state.sucursales.configuracion.productoInventario

    const productoControlaStock = this.producto?.controlaStock ?? false;

    // 2. Si ambos están activos, validamos contra el stock disponible
    if (moduloInventarioActivo && productoControlaStock) {
        const stockDisponible = Number(this.producto?.cantidadstock ?? 0);

        if (nuevaCantidad > stockDisponible) {
            console.warn(`🚫 Stock insuficiente para ${this.descripcion_concepto}. Disponible: ${stockDisponible}`);
            // Podrías optar por setear al máximo o simplemente no incrementar
            this.cantidad = stockDisponible; 
            this.normalizarCantidad();
            return; 
        }
    }

    // 3. Si no controla stock o hay suficiente, procedemos
    this.cantidad = nuevaCantidad;
    this.normalizarCantidad();
}

    decrementar() {
        const paso = this.getPasoCantidad();
        if (this.cantidad <= paso) return;
        this.cantidad -= paso;
        this.normalizarCantidad();
    }

    redondear(valor) {
        return (this.config.tipoRedondeo || "decimales") === "entero"
            ? Math.round(valor)
            : Number(valor.toFixed(2));
    }

    getIvaValue() {
        if (!this.vat_type?.description) return 0;
        const v = parseFloat(this.vat_type.description);
        return isNaN(v) ? 0 : v;
    }
}